import { useState, useMemo } from 'react'
import { useScan } from '../hooks/useScan'
import { StrategyDetailModal } from './StrategyDetailModal'
import type { Strategy } from '../api'

interface ScanResultsProps {
  underlying?: string
  top?: number
}

type SortKey = 'ev_per_dollar' | 'ev' | 'ev_pct' | 'max_loss' | 'dte' | 'strike'

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'ev_pct', label: 'EV%' },
  { key: 'ev_per_dollar', label: 'EV/$' },
  { key: 'ev', label: 'EV' },
  { key: 'max_loss', label: 'Max Loss' },
  { key: 'dte', label: 'DTE' },
  { key: 'strike', label: 'Strike' },
]

function formatDate(s?: string | null) {
  if (!s) return '—'
  try {
    const d = new Date(s)
    if (isNaN(d.getTime())) return s
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return s
  }
}

function fmtNum(v?: number | null, decimals = 2, prefix = ''): string {
  if (v == null || isNaN(v)) return '—'
  return `${prefix}${Number(v).toFixed(decimals)}`
}

function fmtPct(v?: number | null): string {
  if (v == null || isNaN(v)) return '—'
  return `${(v * 100).toFixed(1)}%`
}

function rankBadge(i: number) {
  if (i === 0) return <span className="badge bg-ev-gold/20 text-ev-gold border border-ev-gold/40">🥇 #1</span>
  if (i === 1) return <span className="badge bg-ev-silver/20 text-ev-silver border border-ev-silver/40">🥈 #2</span>
  if (i === 2) return <span className="badge bg-ev-bronze/20 text-ev-bronze border border-ev-bronze/40">🥉 #3</span>
  return <span className="font-mono text-xs text-gray-500">#{i + 1}</span>
}

function moneynessBadge(m?: string) {
  if (!m) return null
  const cls = m === 'ITM' ? 'badge-itm' : m === 'ATM' ? 'badge-atm' : 'badge-otm'
  return <span className={`badge ${cls}`}>{m}</span>
}

function creditBadge(isCredit?: boolean) {
  if (isCredit === undefined) return null
  return isCredit
    ? <span className="rounded bg-emerald-600/20 px-1.5 py-0.5 text-[10px] font-bold text-emerald-400 border border-emerald-600/40">CREDIT</span>
    : <span className="rounded bg-blue-600/20 px-1.5 py-0.5 text-[10px] font-bold text-blue-400 border border-blue-600/40">DEBIT</span>
}

function evPctColor(v?: number) {
  if (v == null || isNaN(v)) return 'text-gray-400'
  if (v > 3) return 'text-emerald-400'
  if (v >= 1) return 'text-yellow-400'
  return 'text-red-400'
}

function fmtEvPct(v?: number): string {
  if (v == null || isNaN(v)) return '—'
  return `${v.toFixed(2)}%`
}

function stratColor(t: string) {
  if (t.includes('BULL') || t.includes('CALL_SPREAD')) return 'text-emerald-400'
  if (t.includes('BEAR') || t.includes('PUT_SPREAD')) return 'text-rose-400'
  if (t.includes('STRADDLE') || t.includes('STRANGLE')) return 'text-purple-400'
  if (t.includes('IRON')) return 'text-sky-400'
  return 'text-amber-400'
}

function stratBg(t: string) {
  if (t.includes('BULL') || t.includes('CALL_SPREAD')) return 'border-emerald-700/30 bg-emerald-900/10'
  if (t.includes('BEAR') || t.includes('PUT_SPREAD')) return 'border-rose-700/30 bg-rose-900/10'
  if (t.includes('STRADDLE') || t.includes('STRANGLE')) return 'border-purple-700/30 bg-purple-900/10'
  if (t.includes('IRON')) return 'border-sky-700/30 bg-sky-900/10'
  return 'border-amber-700/30 bg-amber-900/10'
}

export function ScanResults({ underlying = 'BTCUSDT', top = 20 }: ScanResultsProps) {
  const { data, loading, error, run } = useScan({ underlying, top: String(top) })
  const [sortBy, setSortBy] = useState<SortKey>('ev_pct')
  const [selected, setSelected] = useState<Strategy | null>(null)

  const sorted = useMemo(() => {
    if (!data?.strategies) return []
    const arr = [...data.strategies]
    arr.sort((a, b) => {
      const aV = (a[sortBy] ?? 0) as number
      const bV = (b[sortBy] ?? 0) as number
      if (sortBy === 'max_loss' || sortBy === 'dte') return (aV ?? 0) - (bV ?? 0)
      return (bV ?? 0) - (aV ?? 0)
    })
    return arr
  }, [data, sortBy])

  return (
    <>
      <div className="card">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-bold text-white">Scan Results</h2>
            <p className="text-[11px] text-gray-500">{underlying} · {data?.count ?? 0} strategies</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[11px] text-gray-500">Sort:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortKey)}
              className="rounded-md border border-ev-border bg-ev-surface px-2 py-1 text-xs text-gray-300 focus:border-ev-accent focus:outline-none"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={() => run({ underlying, top: String(top) })}
              disabled={loading}
              className="rounded-md bg-ev-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Scanning...' : '↻ Refresh'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
            {error}
          </div>
        )}

        {!loading && data && data.count === 0 && (
          <p className="py-8 text-center text-sm text-gray-500">No strategies found.</p>
        )}

        {data && data.count > 0 && (
          <>
            {/* Mobile card layout */}
            <div className="space-y-2.5 md:hidden">
              {sorted.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setSelected(s)}
                  className={`w-full rounded-xl border p-3.5 text-left transition-all active:scale-[0.98] hover:brightness-110 ${stratBg(s.strategy_type)}`}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {rankBadge(i)}
                      <span className={`text-xs font-bold ${stratColor(s.strategy_type)}`}>
                        {s.strategy_type?.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <svg className="h-4 w-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </div>
                  <div className="grid grid-cols-3 gap-x-3 gap-y-1.5 text-xs">
                    <div>
                      <span className="text-gray-500">Strike</span>
                      <span className="ml-1.5 font-mono font-semibold text-white">
                        {s.strike != null ? `$${Number(s.strike).toLocaleString()}` : '—'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">DTE</span>
                      <span className="ml-1.5 font-mono text-gray-300">{s.dte ?? '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Prob</span>
                      <span className="ml-1.5 font-mono text-gray-300">{fmtPct(s.probability)}</span>
                    </div>
                    <div className="col-span-3 flex items-center justify-between border-t border-ev-border/50 pt-1.5 mt-1">
                      <div>
                        <span className="text-[10px] text-gray-500">EV%</span>
                        <span className={`ml-1 font-mono text-sm font-bold ${evPctColor(s.ev_pct)}`}>
                          {fmtEvPct(s.ev_pct)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] text-gray-500">EV/$</span>
                        <span className="ml-1 highlight-ev rounded px-1.5 py-0.5 font-mono text-xs font-bold">
                          {fmtNum(s.ev_per_dollar, 4)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] text-gray-500">Loss</span>
                        <span className="ml-1 font-mono text-xs text-red-400">
                          {fmtNum(s.max_loss, 0, '$')}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Desktop table layout */}
            <div className="table-scroll hidden md:block">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-ev-border text-[10px] uppercase tracking-wider text-gray-500">
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Rank</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Strategy</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Symbol</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Strike</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Expiry</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">DTE</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Mark</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">EV</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">EV%</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Max Loss</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Max Profit</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold text-ev-green">EV/$</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Fees</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Prob</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Money</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Bid/Ask</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Vol</th>
                    <th className="whitespace-nowrap px-2 py-2.5 font-semibold">OI</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((s, i) => {
                    const rowCls = i < 3 ? `rank-${i + 1}` : ''
                    return (
                      <tr
                        key={i}
                        onClick={() => setSelected(s)}
                        className={`cursor-pointer border-b border-ev-border/50 last:border-0 row-hover ${rowCls}`}
                      >
                        <td className="whitespace-nowrap px-2 py-2.5">{rankBadge(i)}</td>
                        <td className={`whitespace-nowrap px-2 py-2.5 font-semibold ${stratColor(s.strategy_type)}`}>
                          <span className="flex items-center gap-1.5">
                            {s.strategy_type}
                            {creditBadge(s.is_credit)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-2 py-2.5 font-mono text-[11px] text-gray-400">
                          {s.symbol ?? underlying}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2.5 font-mono">
                          {s.strike != null ? Number(s.strike).toLocaleString() : '—'}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2.5 text-gray-400">{formatDate(s.expiry)}</td>
                        <td className="whitespace-nowrap px-2 py-2.5 font-mono text-gray-400">
                          {s.dte ?? '—'}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2.5 font-mono font-medium">
                          {s.mark != null ? `$${Number(s.mark).toFixed(2)}` : '—'}
                        </td>
                        <td className={`whitespace-nowrap px-2 py-2.5 font-mono font-bold ${(s.ev ?? 0) >= 0 ? 'text-ev-green' : 'text-ev-red'}`}>
                          {(s.ev ?? 0) >= 0 ? '+' : ''}{fmtNum(s.ev, 4)}
                        </td>
                        <td className={`whitespace-nowrap px-2 py-2.5 font-mono font-bold ${evPctColor(s.ev_pct)}`}>
                          {fmtEvPct(s.ev_pct)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2.5 font-mono text-red-400">
                          {fmtNum(s.max_loss, 2, '$')}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2.5 font-mono text-emerald-400">
                          {s.max_profit != null && !isNaN(s.max_profit) ? `$${Number(s.max_profit).toFixed(2)}` : '—'}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2.5 highlight-ev rounded font-mono">
                          {fmtNum(s.ev_per_dollar, 4)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2.5 font-mono text-gray-400">
                          ${fmtNum(s.total_fees, 4)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2.5 font-mono text-gray-400">
                          {fmtPct(s.probability)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2.5">
                          {moneynessBadge(s.moneyness)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2.5 font-mono text-[11px] text-gray-400">
                          {s.bid != null && s.ask != null ? `${Number(s.bid).toFixed(1)} / ${Number(s.ask).toFixed(1)}` : '—'}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2.5 font-mono text-gray-400">
                          {s.volume ?? '—'}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2.5 font-mono text-gray-400">
                          {s.oi ?? '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {selected && (
        <StrategyDetailModal strategy={selected} onClose={() => setSelected(null)} />
      )}
    </>
  )
}
